import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Spinner } from '@/components/ui/Spinner';
import { useAuth } from '@/hooks/auth';
import { useBillingStatementDocument } from '../hooks/use-billing-statement-document';
import { useBillingStatements } from '../hooks/use-billing-statements';
import { useDownloadStatement } from '../hooks/use-download-statement';
import { billingService } from '@/services/billing/billing-service';
import { billingKeys } from '@/services/query-keys/billing-keys';
import { StatementDocumentView } from '../components/StatementDocumentView';
import { CashPoolDocumentView } from '../components/CashPoolDocumentView';
import { DownloadMenu } from '../components/DownloadMenu';
import { MarkPaidModal } from '../components/MarkPaidModal';
import { NotesEditor } from '../components/NotesEditor';
import { PaidSummary } from '../components/PaidSummary';
import { isCashPoolDocument } from '@/types';
import type {
  BillingStatementListItem,
  CashPoolBlock,
  CurrencyBlock,
  PreviewPositionGroup,
  PreviewSection,
  StatementExportFormat,
} from '@/types';

// Formats the export endpoint serves: CSV (LR-505) and PDF (LR-506). 'xlsx' is
// omitted and the backend 400s it — see the modal-era note: product's
// "csv/excel format" reads as the LAYOUT, and the hooks stay parameterised by
// format so a real .xlsx writer is a one-line addition once the answer lands.
const DOWNLOAD_FORMATS: { format: StatementExportFormat; label: string }[] = [
  { format: 'csv', label: 'CSV' },
  { format: 'pdf', label: 'PDF' },
];

/**
 * The header's download menu, together with the export it runs (LR-728).
 *
 * Its own component so that the export's once-a-second progress re-renders the
 * menu, not the document below it: for a counterparty with tens of thousands of
 * positions one render of that document takes longer than the poll interval,
 * and the page would never catch up. `visible` hides it without unmounting it,
 * so a hidden menu still carries a running export's progress.
 *
 * The export can only ever be of the document on screen: the route remounts
 * this page per statementId (`key` in router.tsx), so opening another document
 * ends the wait here - the build continues on the server, and clicking the same
 * format again joins it, which is what the KB promises and all it promises.
 * Following an export across documents is the export tray, deliberately out of
 * scope (see `useBackgroundExport`).
 */
const StatementDownload: React.FC<{ id: number; visible: boolean }> = ({
  id,
  visible,
}) => {
  const { download, isDownloading, exportJob } = useDownloadStatement();
  // A running export keeps the menu on screen even when the document behind it
  // has nothing to download: hiding the trigger would take the progress bar and
  // the live region with it, leaving the trader to conclude the export died.
  if (!visible && !isDownloading) return null;
  return (
    <DownloadMenu
      isDownloading={isDownloading}
      exportJob={exportJob}
      options={DOWNLOAD_FORMATS.map(({ format, label }) => ({
        format,
        label,
        onSelect: () => download(id, format),
      }))}
    />
  );
};

/**
 * The billing document page (LR-557): one document per counterparty per period,
 * addressed by any member statement id (`/billing/:statementId`). Replaces the
 * per-statement detail modal — the document has grown per-currency blocks and
 * four sections each, which no longer fit a modal.
 *
 * Two reads compose it: the DOCUMENT (structure: blocks → sections → rows,
 * every subtotal computed server-side) and the STATEMENTS LIST narrowed to the
 * same counterparty + period (settlement facts: paid date/amount, notes) —
 * `CurrencyBlock` deliberately carries no payment fields. They join on
 * `statement_id`, and the per-block extras (Mark paid, paid strip, note
 * editor) degrade to view-only if the list row has not arrived.
 */
export const BillingDocumentPage: React.FC = () => {
  const { statementId } = useParams({ from: '/billing/$statementId' });
  // isSafeInteger, not just digits: past 2^53 Number() rounds a run of digits
  // to a *different* id, and the page would silently fetch a neighbouring
  // statement (LR-551 class).
  const parsed = /^\d+$/.test(statementId) ? Number(statementId) : NaN;
  const id = Number.isSafeInteger(parsed) ? parsed : null;
  const queryClient = useQueryClient();

  const {
    data: doc,
    isLoading,
    error,
    dataUpdatedAt,
  } = useBillingStatementDocument(id);
  // LR-558: the endpoint serves two document shapes. Narrow once here so the
  // rest of the page reads whichever it got without re-checking.
  const cashPool = isCashPoolDocument(doc) ? doc : null;
  // Whether the document has anything to download at all. Both shapes count it
  // in blocks now, but they are different blocks — a pool block is a currency's
  // statement, a trade block is a currency+agreement+account one.
  const hasContent = doc ? doc.blocks.length > 0 : false;
  // Counterparty + period, wherever they live on the shape we got.
  const about = doc
    ? isCashPoolDocument(doc)
      ? {
          counterpartyId: doc.counterparty_id,
          counterpartyName: doc.counterparty_name,
          periodStart: doc.period_start,
          periodEnd: doc.period_end,
        }
      : {
          counterpartyId: doc.header.counterparty_id ?? undefined,
          counterpartyName: doc.header.counterparty_name,
          periodStart: doc.header.period_start ?? undefined,
          periodEnd: doc.header.period_end ?? undefined,
        }
    : null;
  // Settlement facts for every block of the document in one request. The list
  // endpoint filters on period_start, so gte = lte = the document's own start.
  const { data: statements } = useBillingStatements(
    about
      ? {
          counterparty: about.counterpartyId ?? undefined,
          period_start_gte: about.periodStart,
          period_start_lte: about.periodStart,
        }
      : undefined,
    // The filters come from the document itself — don't fire an unfiltered
    // page-1 request while it is still loading.
    { enabled: doc !== undefined },
  );
  // LR-680: same gate as BillingPage — settlement writes need `create_trades`.
  // The document itself and its downloads are reads and stay open to everyone.
  const canTrade = useAuth().hasPermission('create_trades');
  // Snapshot the row when Mark paid opens (LR-556): a list refetch while the
  // modal is on screen must not swap or clear its statement.
  const [markPaidFor, setMarkPaidFor] = useState<BillingStatementListItem | null>(
    null,
  );

  // The document scrolls in the page body; `null` until that element mounts.
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  // The header box the trade document puts its search field in.
  const [searchSlot, setSearchSlot] = useState<HTMLDivElement | null>(null);
  // Unsaved notes, by statement: the editor lives in a block heading, which the
  // document unmounts once it scrolls out of view.
  const [noteDrafts] = useState(() => new Map<number, string>());

  // What the document is handed stays the same object across renders that do
  // not change it (Mark paid opening, say): the document is memoized, and on a
  // large book one needless render of it is felt.
  const rowsById = useMemo(
    () => new Map((statements?.results ?? []).map((s) => [s.id, s] as const)),
    [statements],
  );

  // A draft the list now carries as saved is no longer a draft. The editor lets
  // it go itself, but only while mounted: saved and then scrolled away before
  // the refetch, it would come back later as an unsaved edit over whatever note
  // has been saved since - one Save from overwriting it.
  useEffect(() => {
    for (const [statementId, draft] of noteDrafts) {
      const row = rowsById.get(statementId);
      if (row && (row.notes ?? '') === draft) noteDrafts.delete(statementId);
    }
  }, [rowsById, noteDrafts]);

  // The document arrives footed but without daily lines (LR-571) - a statement
  // covering a large counterparty's month has hundreds of thousands of them. A
  // group fetches its own when opened, from the block's own statement.
  //
  // Through the query cache, not a bare await: the endpoint answers with EVERY
  // section of the position, so a security footed under two headings is one
  // fetch rather than two identical ones each discarding half. It also survives
  // navigating away and back, which a component-local await does not. Keyed by
  // the document's fetch as well, as in the preview: lines cached or in flight
  // for a previous document must not answer a group the new one re-footed.
  const loadRows = useCallback(
    (block: CurrencyBlock) => async (
      section: PreviewSection,
      group: PreviewPositionGroup,
    ) => {
      const statementId = block.statement_id;
      if (statementId == null) return null;
      const rows = await queryClient.fetchQuery({
        queryKey: billingKeys.positionRows(
          `statement:${statementId}:${dataUpdatedAt}`,
          group.position_id,
        ),
        queryFn: () =>
          billingService.getStatementRows(statementId, group.position_id),
      });
      // The opened section's block only - one position can be footed under two
      // headings (a fee-mode FOP books a fee leg and a rebate leg), and showing
      // both under either would contradict that block's own subtotal.
      return rows.sections.find((s) => s.key === section.key) ?? null;
    },
    [dataUpdatedAt, queryClient],
  );

  /** Settlement controls for one statement id — shared by both document shapes. */
  const renderExtrasFor = useCallback(
    (statementId: number | null, currency: string) => {
      const row = statementId != null ? rowsById.get(statementId) : undefined;
      if (!row) return null;
      return (
        <div className="mb-4">
          <PaidSummary data={row} />
          {canTrade && row.status !== 'paid' && (
            <button
              type="button"
              onClick={() => setMarkPaidFor(row)}
              // Mock header-action style: solid green (#16a34a / #15803d).
              className="rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700"
            >
              Mark paid — {currency}
            </button>
          )}
          <NotesEditor
            statementId={row.id}
            savedNotes={row.notes ?? ''}
            canEdit={canTrade}
            drafts={noteDrafts}
          />
        </div>
      );
    },
    [rowsById, canTrade, noteDrafts],
  );

  const renderBlockExtras = useCallback(
    (block: CurrencyBlock) =>
      renderExtrasFor(block.statement_id, block.currency),
    [renderExtrasFor],
  );

  const renderCashPoolBlockExtras = useCallback(
    (block: CashPoolBlock) =>
      renderExtrasFor(block.statement_id, block.currency),
    [renderExtrasFor],
  );

  return (
    // Bounded to the viewport like the other virtualized pages: the app shell is
    // min-h-screen, so without it the window scrolls and the document windows
    // over its whole height - every group drawn, no sticky headings.
    <div className="flex h-[calc(100vh-0px)] flex-col">
      <div className="flex items-center justify-between gap-4 border-b border-lr-border bg-lr-surface px-6 py-3">
        <div className="flex items-center gap-3">
          <Link
            to="/billing"
            className="inline-flex items-center gap-1.5 text-sm text-lr-muted hover:text-lr-fg"
          >
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
            Billing
          </Link>
          <span className="h-5 w-px bg-lr-border" aria-hidden="true" />
          <h1 className="text-[15px] font-semibold text-lr-fg">
            {about
              ? `${cashPool ? 'Cash Pool Interest' : 'Billing Document'} — ${about.counterpartyName}`
              : 'Billing Document'}
          </h1>
          {about?.periodStart && (
            <span className="whitespace-nowrap text-[12.5px] text-lr-muted">
              {about.periodStart} – {about.periodEnd}
            </span>
          )}
        </div>
        <div className="flex items-center gap-4">
          <div ref={setSearchSlot} className="empty:hidden" />
          {id !== null && <StatementDownload id={id} visible={hasContent} />}
        </div>
      </div>

      {/* The document is a white sheet on the canvas tint, matching how it
          prints — same framing as the month-to-date preview. */}
      <div ref={setScroller} className="flex-1 overflow-auto bg-lr-bg p-6">
        {id === null ? (
          <div className="text-lr-neg">Unknown statement.</div>
        ) : (
          <>
            {isLoading && (
              <div className="flex justify-center py-12">
                <Spinner />
              </div>
            )}
            {error != null && (
              <div className="text-lr-neg">Failed to load the billing document.</div>
            )}
            {doc && (
              <div className="mx-auto max-w-5xl rounded-[10px] border border-lr-border bg-lr-surface px-7 py-6">
                {/* The guard narrows the union for both branches — no cast. */}
                {isCashPoolDocument(doc) ? (
                  <CashPoolDocumentView
                    document={doc}
                    renderBlockExtras={renderCashPoolBlockExtras}
                  />
                ) : (
                  <StatementDocumentView
                    document={doc}
                    renderBlockExtras={renderBlockExtras}
                    loadRows={loadRows}
                    scrollElement={scroller}
                    searchSlot={searchSlot}
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>

      {markPaidFor && (
        <MarkPaidModal
          statement={markPaidFor}
          onClose={() => setMarkPaidFor(null)}
        />
      )}
    </div>
  );
};
